import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardMedia,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { Check } from '@mui/icons-material';
import MKBox from './MKBox';
import MKButton from './MKButton';
import API_BASE_URL from '../apiConfig';

const AvatarSelector = ({ open, onClose, onSelect, currentAvatar, title = "Choose Avatar" }) => {
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchAvatars();
    }
  }, [open]);

  const fetchAvatars = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api/avatars`);
      if (!response.ok) {
        throw new Error('Failed to fetch avatars');
      }
      const data = await response.json();
      setAvatars(data);
      
      // Set current avatar as selected if provided
      if (currentAvatar) {
        const current = data.find(avatar => avatar.url === currentAvatar);
        if (current) {
          setSelectedAvatar(current);
        }
      }
    } catch (err) {
      console.error('Error fetching avatars:', err);
      setError('Failed to load avatars');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarClick = (avatar) => {
    setSelectedAvatar(avatar);
  };

  const handleConfirm = () => {
    if (selectedAvatar) {
      onSelect(selectedAvatar.url);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedAvatar(null);
    onClose();
  };



  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <MKBox sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </MKBox>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error ? (
          <MKBox sx={{ textAlign: 'center', p: 4 }}>
            <Typography color="error">{error}</Typography>
            <MKButton onClick={fetchAvatars} sx={{ mt: 2 }}>
              Retry
            </MKButton>
          </MKBox>
        ) : (
          <MKBox sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {avatars.map((avatar) => (
                <Grid item xs={6} sm={4} md={3} key={avatar.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedAvatar?.id === avatar.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      position: 'relative',
                      '&:hover': {
                        border: '2px solid #1976d2',
                        transform: 'scale(1.02)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                    onClick={() => handleAvatarClick(avatar)}
                  >
                    <CardMedia
                      component="img"
                      height="120"
                      image={avatar.url}
                      alt={avatar.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    {selectedAvatar?.id === avatar.id && (
                      <MKBox
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: '#1976d2',
                          color: 'white',
                          borderRadius: '50%',
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Check sx={{ fontSize: 16 }} />
                      </MKBox>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {avatars.length === 0 && (
              <MKBox sx={{ textAlign: 'center', p: 4 }}>
                <Typography color="text.secondary">
                  No avatars available
                </Typography>
              </MKBox>
            )}
          </MKBox>
        )}
      </DialogContent>
      <DialogActions>
        <MKButton onClick={handleClose}>Cancel</MKButton>
        <MKButton 
          onClick={handleConfirm} 
          variant="contained" 
          disabled={!selectedAvatar}
        >
          Select Avatar
        </MKButton>
      </DialogActions>
    </Dialog>
  );
};

export default AvatarSelector; 